import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { AssetId } from '../assets/AssetTypes';
import type { EntityVisual } from './EntityVisual';
import type { VisualFactory } from './VisualFactory';

/**
 * Object pool for visuals, keyed by asset id. The endless track spawns and
 * recycles hundreds of objects per minute – creating/disposing scene nodes
 * each time would cause GC spikes and frame hitches.
 */
export class VisualPool {
  private readonly free = new Map<AssetId, EntityVisual[]>();
  private created = 0;
  private inUse = 0;

  constructor(private readonly factory: VisualFactory) {}

  acquire(assetId: AssetId, parent: TransformNode | null): EntityVisual {
    const visual = this.takeFree(assetId) ?? this.create(assetId);
    const root = visual.root;
    root.parent = parent;
    root.position.setAll(0);
    root.rotation.setAll(0);
    root.scaling.setAll(1);
    root.setEnabled(true);
    this.inUse++;
    return visual;
  }

  release(visual: EntityVisual): void {
    this.inUse--;
    if (this.factory.isStale(visual)) {
      this.discard(visual);
      return;
    }
    visual.root.setEnabled(false);
    visual.root.parent = null;
    let list = this.free.get(visual.assetId);
    if (!list) {
      list = [];
      this.free.set(visual.assetId, list);
    }
    list.push(visual);
  }

  prewarm(assetId: AssetId, count: number): void {
    const visuals = Array.from({ length: count }, () => this.acquire(assetId, null));
    for (const visual of visuals) this.release(visual);
  }

  get stats(): { created: number; inUse: number } {
    return { created: this.created, inUse: this.inUse };
  }

  private takeFree(assetId: AssetId): EntityVisual | undefined {
    const list = this.free.get(assetId);
    let visual = list?.pop();
    while (visual && this.factory.isStale(visual)) {
      this.discard(visual);
      visual = list?.pop();
    }
    return visual;
  }

  private discard(visual: EntityVisual): void {
    visual.dispose();
    this.created--;
  }

  private create(assetId: AssetId): EntityVisual {
    this.created++;
    return this.factory.create(assetId);
  }
}
